import { PUSH_TARGET_CUSTOM, PUSH_TARGET_NONE, findPushTarget } from "../model/pushTargets";

/**
 * Turn the four target fields into the one `url` the API takes — or `undefined`, which is what
 * "Nur App öffnen" means: the DTO omits the key and the backend falls back to `/`.
 *
 * Declared here rather than imported from the form schema so the schema can call this while
 * validating without the two files importing each other.
 */
export interface PushTargetValues {
  target: string;
  param1: string;
  param2: string;
  customUrl: string;
}

/** Values are ids and Auftragsnummern; encoding keeps a stray `/` or space out of the address. */
const readParam = (values: PushTargetValues, field: "param1" | "param2") =>
  encodeURIComponent(values[field].trim());

export function buildPushTargetUrl(values: PushTargetValues): string | undefined {
  if (values.target === PUSH_TARGET_NONE) return undefined;
  if (values.target === PUSH_TARGET_CUSTOM) {
    const custom = values.customUrl.trim();
    return custom === "" ? undefined : custom;
  }

  const target = findPushTarget(values.target);
  if (!target) return undefined;

  // A param with no token is a lookup aid — it found the id in the field next to it and has no
  // business in the address.
  const substituted = (target.params ?? []).filter((param) => param.token);

  // Half a URL is not a URL: while an id is still missing there is nothing to show the sender
  // and nothing to send. The form's own validation is what reports which field is empty.
  if (substituted.some((param) => values[param.field].trim() === "")) return undefined;

  return substituted.reduce(
    (url, param) => url.replace(param.token!, readParam(values, param.field)),
    target.path,
  );
}
